import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formatNumber } from '@/libs/format';
import { Button } from '@/shared/components/Button';
import { normalizePhone } from './helpers/normalizePhone';
import { OTPInput } from './ui/OTPInput';
type PhoneCodeFormValues = {
  otp: string;
};
interface PhoneCodeFormProps {
  phone: string;
  caption?: string;
  onSubmitting: (data: {
    otp: string;
  }, setError?: (message: string) => void) => void;
  /** Опционально: повторная отправка кода. Если не передан, кнопка «Отправить снова» отключена. */
  onResend?: (phone: string) => Promise<{
    error?: string;
  } | void>;
}
const TIME_LEFT = 30;
export const PhoneCodeForm = ({
  phone,
  caption,
  onSubmitting,
  onResend
}: PhoneCodeFormProps) => {
  const t = useTranslations('Auth');
  const [timeLeft, setTimeLeft] = useState(TIME_LEFT);
  const [isDisabled, setIsDisabled] = useState(true);
  const [resending, setResending] = useState(false);
  const {
    handleSubmit,
    formState: {
      errors,
      isLoading
    },
    control,
    setError,
    reset
  } = useForm<PhoneCodeFormValues>({
    mode: 'onChange',
    defaultValues: {
      otp: ''
    }
  });
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => prev <= 0 ? 0 : prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (timeLeft === 0) {
      setIsDisabled(false);
    }
  }, [timeLeft]);
  const handleResendCode = async () => {
    if (!onResend) return;
    try {
      setResending(true);
      setIsDisabled(true);
      setTimeLeft(TIME_LEFT);
      reset();
      const result = await onResend(normalizePhone(phone));
      const err = result && typeof result === 'object' && 'error' in result ? result.error : undefined;
      if (err) {
        setError('otp', {
          message: err
        });
      }
    } catch (error) {
      setError('otp', {
        message: error instanceof Error ? error.message : t('Errors.AnUnknownErrorOccurred')
      });
    } finally {
      setResending(false);
      setIsDisabled(false);
      setTimeLeft(TIME_LEFT);
    }
  };
  const onSubmit = async (data: PhoneCodeFormValues) => {
    try {
      await onSubmitting(data, message => setError('otp', {
        message
      }));
    } catch (error_0) {
      setError('otp', {
        message: error_0 instanceof Error ? error_0.message : t('Errors.AnUnknownErrorOccurred')
      });
    }
  };
  const canResend = !!onResend && !isDisabled;
  const showResendTimer = !!onResend;
  return <div className="flex flex-col items-center justify-center gap-6 tablet:gap-10">
      <div className="flex flex-col items-center justify-center">
        <div className="flex w-full flex-col items-center justify-center">
          <h1 className="text-fill-transparent bold tablet:leading-1 w-full bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text text-center font-secondary text-[30px] font-extrabold uppercase italic leading-none tablet:text-nowrap tablet:text-[34px]">
            {t('CodeSentToNumber')}
          </h1>
          <span className="text-center font-secondary text-[30px] font-extrabold italic text-white tablet:text-[34px]">
            {formatNumber(phone)}
          </span>
        </div>
        {caption && <span className="mt-4 text-center text-[16px] text-[#AAAAB6]">{caption}</span>}
      </div>
      <div className="flex flex-col items-center justify-center gap-4">
        <Controller control={control} name="otp" render={({
        field
      }) => <OTPInput errorMessage={errors.otp?.message} {...field} />} />
        {showResendTimer && <button type="button" onClick={handleResendCode} disabled={!canResend || resending} className={`font-secondary font-bold uppercase ${timeLeft > 0 ? '!text-[#AAAAB6]' : '!text-white'}`}>
            {resending ? '...' : timeLeft > 0 ? `${t('ResendCodeIn')} 00:${timeLeft.toString().padStart(2, '0')}` : t('SendCodeAgain')}
          </button>}
      </div>
      <Button size="2md" loading={isLoading} variant="white" className="w-full" onClick={handleSubmit(onSubmit)}>
        {t('Continue')}
      </Button>
    </div>;
};